import { MatchingTxLog } from '../../../../types/rest/mockServiceTypes';
import { MatchRow } from './MockServiceLogs';
import { Button } from 'react-bootstrap';
import clsx from 'clsx';
import BzmIcon from '../../../../components/icons/BzmIcon';
import React from 'react';
import _ from 'lodash';

/**
 * Escapes special characters in a string to be used in a regular expression.
 *
 * @param {string} string - The string to escape.
 * @returns {string} The escaped string.
 */
export const escapeRegExp = (string: string): string => {
  if (_.isNil(string)) {
    return '';
  }
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || searchTerm.trim() === '') return text;

  const parts = String(text).split(new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi'));

  return parts
    .map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? `<mark class="highlightedText" style="padding: 2px; padding-right: 0; padding-left: 0; background: #fbe59d" key=${index}>${part}</mark>`
        : part,
    )
    .join('');
};

/**
 * Converts a JSON object to a string representation.
 *
 * @param {object} jsonObj - The JSON object to convert.
 * @returns {string} The string representation of the JSON object.
 */
export const getStringRepresentation = (jsonObj: any): string => {
  let displayVal = ' ';
  for (const key in jsonObj) {
    if (jsonObj.hasOwnProperty(key)) {
      displayVal += key + ' -> ' + jsonObj[key] + ' , ';
    }
  }
  return displayVal.slice(0, -2);
};

/**
 * Displays a JSON object as a string with optional highlighted search term.
 *
 * @param {object} jsonObj - The JSON object to display.
 * @param {string} searchTerm - The term to highlight in the JSON string representation.
 */
export const displayJsonDangerous = (jsonObj: any, searchTerm: string): any => {
  const stringRepresentation = getStringRepresentation(jsonObj);
  return !searchTerm || searchTerm.trim() === ''
    ? stringRepresentation
    : highlightText(stringRepresentation, searchTerm);
};

/**
 * Calculates the time differences between log entries and returns an array of MatchRow objects.
 *
 * @param {MatchingTxLog} logStructure - The log structure containing log entries and start time.
 * @returns {MatchRow[]} An array of MatchRow objects with time differences and match descriptions.
 */
export const calculateTimes = (logStructure: MatchingTxLog): MatchRow[] => {
  const results: MatchRow[] = [];
  const { log, st } = logStructure;

  log.forEach((entry, index) => {
    let timeDifference: number;
    if (index === 0) {
      timeDifference = entry.t - st;
    } else {
      timeDifference = entry.t - log[index - 1].t;
    }
    results.push({
      timeMs: timeDifference,
      matchDesc: entry.m,
    });
  });

  return results;
};

export const getArrows = (
  itemName: string,
  title: string,
  markText: boolean,
  expandCollapse: any,
  getItemState: any,
  sTerm: string,
  styles: any,
) => {
  return (
    <Button
      variant={'link'}
      onClick={() => expandCollapse(itemName)}
      className={clsx(styles.borderButton, styles.buttonHoverUnderline, styles.verticalAlignMiddle)}
      style={{
        fontFamily: 'inherit',
        fontWeight: 'bold',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        userSelect: 'text',
        textDecorationColor: 'transparent',
      }}
    >
      {markText === true ? (
        <span
          className={clsx(styles.hoverUnderline, styles.blackFont)}
          dangerouslySetInnerHTML={{ __html: ' ' + highlightText(title, sTerm) }}
        />
      ) : (
        <span className={clsx(styles.hoverUnderline, styles.blackFont)}>{title}</span>
      )}

      <BzmIcon
        className={clsx('icon-sm ml-1', styles.chevronIcon, styles.borderLeft)}
        variant={getItemState(itemName) ? 'icon-bzm-chevron-down' : 'icon-bzm-chevron-right'}
      />
    </Button>
  );
};
